export type GoogleAnalyticsConsentChoice = "unknown" | "accepted" | "rejected";

export const GOOGLE_ANALYTICS_CONSENT_KEY = "coachveera.google-analytics-consent.v1";

const publicAnalyticsPaths = new Set([
  "/",
  "/pricing",
  "/trust",
  "/privacy",
  "/terms",
  "/docs/zoom-integration",
]);

const publicPageTitles: Record<string, string> = {
  "/": "Coach Veera | Coaching Continuity",
  "/pricing": "Pricing | Coach Veera",
  "/trust": "Trust & Security | Coach Veera",
  "/privacy": "Privacy Policy | Coach Veera",
  "/terms": "Terms of Service | Coach Veera",
  "/docs/zoom-integration": "Zoom Integration Guide | Veera",
};

type GoogleTagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

export function crossesTranscriptDocumentBoundary(previousPath: string, nextPath: string) {
  return (previousPath === "/transcript-upload") !== (nextPath === "/transcript-upload");
}

export function isPublicGoogleAnalyticsPath(pathname: string) {
  return publicAnalyticsPaths.has(pathname);
}

export function getPublicGoogleAnalyticsPageTitle(pathname: string) {
  return publicPageTitles[pathname] ?? "Coach Veera";
}

export function isAnalyticsConsentWithdrawal(
  current: GoogleAnalyticsConsentChoice,
  next: GoogleAnalyticsConsentChoice,
) {
  return current === "accepted" && next !== "accepted";
}

export function readGoogleAnalyticsConsent(): GoogleAnalyticsConsentChoice {
  if (typeof window === "undefined") return "unknown";

  try {
    const choice = window.localStorage.getItem(GOOGLE_ANALYTICS_CONSENT_KEY);
    return choice === "accepted" || choice === "rejected" ? choice : "unknown";
  } catch {
    return "unknown";
  }
}

export function saveGoogleAnalyticsConsent(choice: Exclude<GoogleAnalyticsConsentChoice, "unknown">) {
  try {
    window.localStorage.setItem(GOOGLE_ANALYTICS_CONSENT_KEY, choice);
  } catch {
    // If storage is unavailable, the in-memory choice still applies for this visit.
  }
}

function getGtagWindow() {
  const target = window as GoogleTagWindow;
  target.dataLayer = target.dataLayer || [];
  target.gtag = target.gtag || function gtag() {
    // Google tag commands require the native Arguments object, not an Array.
    // eslint-disable-next-line prefer-rest-params
    target.dataLayer?.push(arguments);
  };
  return target;
}

export function loadGoogleAnalytics(
  measurementId: string,
  pathname: string,
  pageTitle: string,
  referrer: string,
) {
  if (typeof window === "undefined" || !measurementId) return;

  const scriptId = "coachveera-google-analytics-tag";
  const target = getGtagWindow();
  Reflect.set(target, `ga-disable-${measurementId}`, false);

  const hasScript = Boolean(document.getElementById(scriptId));
  if (!hasScript) {
    target.gtag?.("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    target.gtag?.("js", new Date());
  }

  // Keep automatic events on the current sanitized virtual page too.
  target.gtag?.("config", measurementId, {
    ...(hasScript ? { update: true } : {}),
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: `${window.location.origin}${pathname}`,
    page_title: pageTitle,
    page_referrer: referrer,
  });

  if (!hasScript) {
    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }
}

export function sendPublicPageView(measurementId: string, pathname: string, pageTitle: string, referrer?: string) {
  if (!isPublicGoogleAnalyticsPath(pathname) || typeof window === "undefined") return;

  const target = window as GoogleTagWindow;
  if (!target.gtag) return;

  target.gtag("event", "page_view", {
    send_to: measurementId,
    page_title: pageTitle,
    page_path: pathname,
    page_location: `${window.location.origin}${pathname}`,
    ...(referrer !== undefined ? { page_referrer: referrer } : {}),
  });
}

export function updateGoogleAnalyticsConsent(granted: boolean, measurementId: string) {
  if (typeof window !== "undefined") {
    const target = window as GoogleTagWindow;
    // Block sends and cookie writes synchronously, including unload events.
    Reflect.set(target, `ga-disable-${measurementId}`, !granted);
    target.gtag?.("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }

  if (!granted) clearGoogleAnalyticsCookies();
}

function clearGoogleAnalyticsCookies() {
  if (typeof document === "undefined") return;

  const names = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name && (name === "_ga" || name.startsWith("_ga_"))));

  const domains = ["", "; Domain=coachveera.com", "; Domain=.coachveera.com"];
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax; Secure`;
    }
  }
}

export function getPublicGoogleAnalyticsReferrer(
  previousPublicPath: string | null,
  crossedPrivateRoute = false,
  origin?: string,
  documentReferrer?: string,
) {
  if (previousPublicPath && isPublicGoogleAnalyticsPath(previousPublicPath)) {
    const currentOrigin = origin ?? (typeof window !== "undefined" ? window.location.origin : undefined);
    return currentOrigin ? `${currentOrigin}${previousPublicPath}` : undefined;
  }

  if (crossedPrivateRoute) return "";

  const currentOrigin = origin ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  if (!currentOrigin) return undefined;

  try {
    const referrer = new URL(documentReferrer ?? (typeof document !== "undefined" ? document.referrer : ""));
    if (referrer.origin === currentOrigin) {
      return isPublicGoogleAnalyticsPath(referrer.pathname) ? `${currentOrigin}${referrer.pathname}` : "";
    }
    return `${referrer.origin}/`;
  } catch {
    return undefined;
  }
}
